import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/config";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="June 24, 2026" testid="privacy-page">
      <p>
        Your recovery is deeply personal, and we treat it that way. This Privacy Policy explains what
        information Not By Will (“we,” “us,” “our”) collects, how we use it, and the choices you have. By
        creating an account or using the app, you agree to this policy.
      </p>

      <LegalSection heading="1. Who this applies to">
        <p>
          This policy covers the Not By Will website and web app at notbywill.com. Not By Will offers
          faith-based support and encouragement for quitting nicotine — it is not a medical or clinical service.
        </p>
      </LegalSection>

      <LegalSection heading="2. Information we collect">
        <p>We collect only what we need to help you on your journey:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Account information</strong> — your name and email address. If you sign in with Google, we
            receive basic Google profile details (name, email, and profile picture).
          </li>
          <li>
            <strong>Your password</strong> — if you sign up with email and password, we store only a securely
            hashed version. We never store or see your actual password.
          </li>
          <li>
            <strong>Recovery data you provide</strong> — your quit plan, nicotine logs, cravings you record,
            journal notes, milestones, and any prayer requests or encouragements you choose to share.
          </li>
          <li>
            <strong>Usage information</strong> — basic analytics about how the app is used (for example, pages
            visited and features used), to help us improve and keep the app reliable.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. How we use your information">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To provide the service, keep you signed in, and save your progress.</li>
          <li>To personalize your plan, encouragement, and the AI Companion’s responses.</li>
          <li>To operate community features like the Prayer Wall when you choose to use them.</li>
          <li>To improve the app, fix problems, and keep it secure.</li>
        </ul>
        <p>We do not sell your personal information, and we never use your recovery data for advertising.</p>
      </LegalSection>

      <LegalSection heading="4. AI Companion and your data">
        <p>
          The Companion feature uses a third-party AI provider (Anthropic’s Claude, accessed through the
          Emergent platform) to generate supportive responses. When you use the Companion, we send only the
          message and the limited context needed to respond. Your conversations are tied to your account and are
          not sold or used to advertise to you.
        </p>
      </LegalSection>

      <LegalSection heading="5. Cookies and sessions">
        <p>
          We use a secure session cookie to keep you signed in. We also use privacy-conscious product analytics
          to understand overall usage. We do not use third-party advertising cookies.
        </p>
      </LegalSection>

      <LegalSection heading="6. How we share information">
        <p>We share information only in limited ways:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong>Service providers</strong> — trusted providers who help us run the app (for example, hosting,
            the AI provider, email delivery, and analytics), who may process data only on our behalf.
          </li>
          <li>
            <strong>Community features</strong> — if you post a prayer request, the first-name or “Someone”
            display you choose is visible to other members. Never include sensitive personal details.
          </li>
          <li>
            <strong>Legal reasons</strong> — if required by law or to protect the safety and rights of people
            using the app.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="7. Data retention and deletion">
        <p>
          We keep your information while your account is active. You can ask us to delete your account and
          associated data at any time by emailing{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-teal-dark font-medium hover:underline">{CONTACT_EMAIL}</a>.
        </p>
      </LegalSection>

      <LegalSection heading="8. Your choices and rights">
        <p>
          You may request access to, correction of, or deletion of your personal information. Depending on where
          you live, you may have additional rights under laws such as the GDPR or CCPA. To make a request, email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-teal-dark font-medium hover:underline">{CONTACT_EMAIL}</a>.
        </p>
      </LegalSection>

      <LegalSection heading="9. Children">
        <p>
          Not By Will is intended for adults. It is not directed to children, and we do not knowingly collect
          information from anyone under 18. If you believe a minor has provided us information, please contact us
          and we will remove it.
        </p>
      </LegalSection>

      <LegalSection heading="10. Security">
        <p>
          We take reasonable measures to protect your information, including hashed passwords and encrypted
          connections. No method of storage or transmission is 100% secure, but we work to safeguard your data.
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to this policy">
        <p>
          We may update this policy from time to time. When we do, we’ll revise the “Last updated” date above and,
          for significant changes, provide a notice in the app.
        </p>
      </LegalSection>

      <LegalSection heading="12. Contact us">
        <p>
          Questions about your privacy? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-teal-dark font-medium hover:underline">{CONTACT_EMAIL}</a>.
        </p>
      </LegalSection>

      <p className="text-sm text-stone/80 border-t border-black/5 pt-6">
        Not By Will offers support and encouragement, not medical advice. See our{" "}
        <Link to="/terms" className="text-brand-teal-dark font-medium hover:underline">Terms of Use</Link>. In an
        emergency, call 911.
      </p>
    </LegalLayout>
  );
}
